import type { ColorPalette } from "../types/narrative";

// Vibrant color palettes - start and end with same color for iOS Safari chrome
export const colorPalettes: ColorPalette[] = [
  { from: "#8B5CF6", via: "#06B6D4", to: "#8B5CF6" }, // violet → cyan → violet
  { from: "#EC4899", via: "#8B5CF6", to: "#EC4899" }, // pink → violet → pink
  { from: "#F97316", via: "#EAB308", to: "#F97316" }, // orange → yellow → orange
  { from: "#06B6D4", via: "#EC4899", to: "#06B6D4" }, // cyan → pink → cyan
  { from: "#10B981", via: "#3B82F6", to: "#10B981" }, // emerald → blue → emerald
  { from: "#EF4444", via: "#F97316", to: "#EF4444" }, // red → orange → red
  { from: "#6366F1", via: "#EC4899", to: "#6366F1" }, // indigo → pink → indigo
  { from: "#14B8A6", via: "#84CC16", to: "#14B8A6" }, // teal → lime → teal
];

// Simple hash function for deterministic palette selection based on section ID
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get a color palette for a section.
 * Priority: 1) Inline palette from section, 2) Deterministic selection based on section ID
 */
export function getPaletteForSection(
  sectionId: string,
  inlinePalette?: ColorPalette,
): ColorPalette {
  // If inline palette is provided, use it
  if (inlinePalette) {
    return inlinePalette;
  }
  // Otherwise, deterministically select based on section ID hash
  const index = hashString(sectionId) % colorPalettes.length;
  return colorPalettes[index];
}

// Legacy: Section-specific color palettes (kept for backward compatibility)
// New narratives should use inline colorPalette in section definitions
export const sectionPalettes: Record<string, ColorPalette> = {
  intro: { from: "#EC4899", via: "#8B5CF6", to: "#EC4899" }, // pink → violet
  table_of_contents: { from: "#8B5CF6", via: "#06B6D4", to: "#8B5CF6" }, // violet → cyan
  personalization: { from: "#F97316", via: "#EAB308", to: "#F97316" }, // orange → yellow
  orientation_leaders: { from: "#10B981", via: "#3B82F6", to: "#10B981" }, // emerald → blue
  efficiency_wins: { from: "#06B6D4", via: "#10B981", to: "#06B6D4" }, // cyan → emerald
  highlight_reel: { from: "#6366F1", via: "#EC4899", to: "#6366F1" }, // indigo → pink
};

// Export function to get theme color for Safari meta tag
export function getThemeColor(colorIndex: number): string {
  return colorPalettes[colorIndex % colorPalettes.length].from;
}

// Get theme color for a specific section (supports inline palette)
export function getSectionThemeColor(
  sectionId: string,
  inlinePalette?: ColorPalette,
): string {
  return getPaletteForSection(sectionId, inlinePalette).from;
}
